import React, { useState } from 'react';
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ForgotPasswordScreen = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, email);
            setSuccess(true);
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/user-not-found') {
                setError(t('auth.error_user_not_found', 'No encontramos una cuenta con este email.'));
            } else if (err.code === 'auth/invalid-email') {
                setError(t('auth.error_invalid_email', 'El email no es válido.'));
            } else {
                setError(t('auth.error_generic', 'Ocurrió un error. Inténtalo de nuevo.'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-2xl p-8 shadow-2xl">
                <div className="mb-6">
                    <Link to="/login" className="text-neutral-400 hover:text-white flex items-center gap-2 text-sm font-bold uppercase tracking-wider transition-colors">
                        <ArrowLeft size={16} />
                        {t('common.back_to_login', 'Volver al Login')}
                    </Link>
                </div>

                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-900 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg shadow-red-900/20">
                        <Mail className="text-white" size={32} />
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase italic tracking-tight mb-2">
                        {t('auth.forgot_password_title', 'Recuperar Contraseña')}
                    </h1>
                    <p className="text-neutral-400 text-sm">
                        {t('auth.forgot_password_desc', 'Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.')}
                    </p>
                </div>

                {success ? (
                    <div className="bg-emerald-900/20 border border-emerald-900/50 rounded-xl p-6 text-center animate-in fade-in zoom-in duration-300">
                        <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                            <CheckCircle className="text-emerald-500" size={24} />
                        </div>
                        <h3 className="text-emerald-400 font-bold mb-2">{t('auth.email_sent', '¡Email enviado!')}</h3>
                        <p className="text-neutral-400 text-xs mb-4">
                            {t('auth.email_sent_desc', 'Revisa tu bandeja de entrada (y spam) para continuar.')}
                        </p>
                        <Link
                            to="/login"
                            className="inline-block bg-emerald-600 hover:bg-emerald-500 text-black font-bold py-2 px-6 rounded-lg uppercase tracking-wider text-xs transition-colors"
                        >
                            {t('auth.login_now', 'Ir al Login')}
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-3 flex items-start gap-3">
                                <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={16} />
                                <p className="text-red-400 text-xs font-medium">{error}</p>
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-neutral-500 uppercase ml-1">
                                {t('auth.email_label', 'Email')}
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@email.com"
                                className="w-full bg-black border border-neutral-800 rounded-xl p-4 text-white placeholder:text-neutral-700 focus:border-red-600 focus:outline-none transition-colors"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-4 rounded-xl uppercase tracking-wider text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-900/20 hover:shadow-red-600/20"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                                    {t('common.sending', 'Enviando...')}
                                </span>
                            ) : (
                                t('auth.send_link', 'Enviar Enlace')
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ForgotPasswordScreen;
